import styled from "styled-components";
import videojs from "video.js";
import { Flex } from "rebass/styled-components";
import { memo } from "react";
import { useRecoilValue } from "recoil";
import { isMobile } from "react-device-detect";
import { useStatsigClient } from "@statsig/react-bindings";

import { ShareDialogue } from "../ShareDialogue";
import { MatchHistoryWidget } from "../MatchHistoryWidget";
import { EditPopUp } from "../EditPopUp";
import { ButtonMenu } from "../ButtonMenu";
import { IClip } from "MVVM/@types";
import { SharePopUp } from "../SharePopUp";
import { TUserRole } from "../../@types";

import { ClipStats } from "../ClipStats";
import { MobileAdBanner } from "../MobileAdBanner";
import { NextUpPopUp } from "../NextUpPopUp";
import { NotificationBanner } from "../NotificationBanner";
import { NotificationButton } from "../NotificationButton";
import { useWindowSize } from "MVVM/Hooks/useWindowSize";
import { PlaylistIdState } from "MVVM/State/embeddablePlaylist";
import "./index.css";
import { ShouldDisplayOverlayState } from "MVVM/State/iframe";

const Banner = memo(
  ({ clip, userRole = "creator" }: { clip: IClip; userRole?: TUserRole }) => {
    const playlistId = useRecoilValue(PlaylistIdState);
    const { width } = useWindowSize();
    if (isMobile) return <MobileAdBanner clip={clip} />;

    if (!!playlistId && width <= 370) return <NotificationButton clip={clip} />;

    return <NotificationBanner clip={clip} userRole={userRole} />;
  },
);

export const Overlay = memo(
  ({
    player,
    clip,
    userRole = "creator",
    hideNextUpThumbnail = false,
  }: {
    player: videojs.Player | null;
    clip: IClip;
    userRole?: TUserRole;
    hideNextUpThumbnail?: boolean;
  }) => {
    const shouldDisplayOverlay = useRecoilValue(ShouldDisplayOverlayState);
    const { getExperiment } = useStatsigClient();

    const showSocialsV2 = getExperiment("socials_in_iframe_v2").get(
      "show_social_signals",
    );

    return (
      <StyledContainer className="overlay" $shouldShow={shouldDisplayOverlay}>
        <Banner clip={clip} userRole={userRole} />
        <LeftContainer className="left-container">
          <ButtonMenu player={player} clip={clip} />
        </LeftContainer>
        <ContentDisplay className="content-display">
          <ShareDialogue clip={clip} />
          <MatchHistoryWidget clip={clip} userRole={userRole} />
          <SharePopUp clip={clip} />
          <EditPopUp clip={clip} />
          <NextUpPopUp clip={clip} showThumbnail={!hideNextUpThumbnail} />
        </ContentDisplay>
        {showSocialsV2 && (
          <BottomRightContainer>
            <ClipStats clip={clip} />
          </BottomRightContainer>
        )}
      </StyledContainer>
    );
  },
);

const ContentDisplay = styled.div`
  > * {
    pointer-events: auto;
  }
`;

const LeftContainer = styled(Flex)`
  align-items: center;
  flex-direction: row;
  height: 100%;
  justify-content: center;
  left: 0;
  position: absolute;
  top: 0;

  @media (min-width: 650px) {
    margin-top: 0;
  }

  @media (min-width: 1200px) {
    margin-top: 0;
  }
`;

const StyledContainer = styled(Flex)<{ $shouldShow: boolean }>`
  align-items: center;
  container-name: player-overlay;
  container-type: size;
  height: 100%;
  justify-content: flex-start;
  left: 0;
  opacity: ${({ $shouldShow }) => ($shouldShow ? 1 : 0)};
  pointer-events: none;
  position: absolute;
  top: 0;
  transition:
    opacity 0.3s ease-in-out,
    visibility 0.3s ease-in-out;
  visibility: ${({ $shouldShow }) => ($shouldShow ? "visible" : "hidden")};
  width: 100%;
  z-index: 2;
`;

const BottomRightContainer = styled(Flex)`
  bottom: 35px;
  position: absolute;
  right: 0;
`;
